// src/pages/departments/DepartmentsPage/index.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Chip,
  Avatar,
  TextField,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  Extension as ExtensionIcon,
  Search as SearchIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Hooks
import { useDepartments } from '../../hooks/useDepartment';
import { useModules } from '../../../../hooks/api/useModules';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import { useDialog } from '../../../../hooks/mui/useDialog';
import { usePermissions, useAdminModulePermissions } from '../../../auth/hooks/usePermissions';
import { useNonWorkingDays } from '../../../settings/hooks/useNonWorkingDays';

// Components
import DepartmentActionMenu from './components/DepartmentActionMenu';
import EditDepartmentDialog from './components/EditDepartmentDialog';
import ModulesManagementDialog from './components/ModulesManagementDialog';
import GenerateHolidaysDialog from './components/GenerateHolidaysDialog';

// Utils
import { UUIDDisplay } from '../../../../utils/formatters/uuid';
import LocalizedDateDistance from '../../../../i18n/LocalizedDateDistance';

import PageHeader from '../../../../components/common/PageHeader';

export default function DepartmentsPage() {
  const { t } = useTranslation(['departments', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = usePermissions();
  // A department's modules are their own admin scopes
  const { canRead: canReadModules, canEnable, canDisable } = useAdminModulePermissions();

  // Dialogs
  const editDialog = useDialog();
  const moduleDialog = useDialog();
  const generateHolidaysDialog = useDialog();

  // Local state
  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [formErrors, setFormErrors] = useState({});
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // API hooks
  const {
    departments,
    loading: departmentsLoading,
    error: departmentsError,
    clearError: clearDepartmentsError,
    fetchAllDepartments,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    deactivateDepartment,
    setGlobalFilesDepartment,
    getDepartmentModules,
    enableModule,
    disableModule,
    resetOverdueTasks,
  } = useDepartments();

  const {
    modules,
    loading: modulesLoading,
    fetchAllModules,
  } = useModules();

  const {
    adminGenerateHolidaysForDepartment,
    loading: generateHolidaysLoading,
  } = useNonWorkingDays();

  useEffect(() => {
    fetchAllDepartments({ include_deactivated: includeDeactivated });
    if (canReadModules) fetchAllModules();
  }, [includeDeactivated, canReadModules]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtered departments
  const filteredDepartments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return departments.filter(dept => {
      if (!includeDeactivated && dept.deactivated_at) return false;
      if (term && !dept.name.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [departments, includeDeactivated, searchTerm]);

  // Form validation
  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = t('departments:form.name_required');
    } else if (formData.name.length > 100) {
      errors.name = t('departments:form.name_max_length');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    fetchAllDepartments({ include_deactivated: includeDeactivated });
  }, [fetchAllDepartments, includeDeactivated]);

  const handleMenuOpen = useCallback((event, department) => {
    event.stopPropagation();
    setSelectedDepartment(department);
    setActionMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedDepartment(null);
  }, []);

  const handleEditClick = useCallback((department) => {
    setFormData({ name: department.name });
    setFormErrors({});
    editDialog.openDialog(department);
  }, [editDialog]);

  const handleModulesClick = useCallback(async (department) => {
    try {
      const result = await getDepartmentModules(department.department_id);
      if (result.success) {
        moduleDialog.openDialog({
          department,
          assignedModules: result.data.modules || [],
        });
      }
    } catch (error) {
      showError(t('departments:errors.load_modules_failed'));
    }
  }, [getDepartmentModules, moduleDialog, showError, t]);

  const handleFormChange = useCallback((field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  const handleEditSubmit = useCallback(async () => {
    if (!validateForm()) return;
    try {
      const result = await updateDepartment(editDialog.data.department_id, formData);
      if (result.success) {
        showSuccess(t('departments:success.updated'));
        editDialog.closeDialog();
        setFormData({ name: '' });
      } else {
        showError(result.error || t('departments:errors.update_failed'));
      }
    } catch (error) {
      showError(t('departments:errors.update_failed'));
    }
  }, [validateForm, updateDepartment, formData, showSuccess, showError, editDialog, t]);

  const handleDeactivate = useCallback((department) => {
    showConfirmDialog({
      title: t('departments:confirm.deactivate_title'),
      message: t('departments:confirm.deactivate_message', { name: department.name }),
      confirmText: t('departments:confirm.deactivate_button'),
      severity: 'warning',
      onConfirm: async () => {
        try {
          const result = await deactivateDepartment(department.department_id);
          if (result.success) {
            showSuccess(t('departments:success.deactivated'));
          } else {
            showError(result.error || t('departments:errors.deactivate_failed'));
          }
        } catch (error) {
          showError(t('departments:errors.deactivate_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deactivateDepartment, showSuccess, showError, handleMenuClose, t]);

  const handleGlobalFiles = useCallback((department) => {
    const manages = !department.manages_global_files;
    const current = departments.find(d => d.manages_global_files && d.department_id !== department.department_id);
    const message = manages
      ? [
        t('departments:confirm.set_global_files_message', { name: department.name }),
        current && t('departments:confirm.set_global_files_replaces', { current: current.name }),
      ].filter(Boolean).join(' ')
      : t('departments:confirm.unset_global_files_message', { name: department.name });
    showConfirmDialog({
      title: t(manages ? 'departments:confirm.set_global_files_title' : 'departments:confirm.unset_global_files_title', { name: department.name }),
      message,
      confirmText: t(manages ? 'departments:confirm.set_global_files_button' : 'departments:confirm.unset_global_files_button'),
      severity: manages ? 'info' : 'warning',
      onConfirm: async () => {
        const result = await setGlobalFilesDepartment(department.department_id, manages);
        if (result.success) {
          showSuccess(t(manages ? 'departments:success.global_files_set' : 'departments:success.global_files_unset', { name: department.name }));
        } else {
          showError(result.error || t('departments:errors.global_files_failed'));
        }
      },
    });
    handleMenuClose();
  }, [departments, showConfirmDialog, setGlobalFilesDepartment, showSuccess, showError, handleMenuClose, t]);

  const handleDelete = useCallback((department) => {
    showConfirmDialog({
      title: t('departments:confirm.delete_title'),
      message: t('departments:confirm.delete_message', { name: department.name }),
      confirmText: t('departments:confirm.delete_button'),
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteDepartment(department.department_id);
          if (result.success) {
            showSuccess(t('departments:success.deleted'));
          } else {
            showError(result.error || t('departments:errors.delete_failed'));
          }
        } catch (error) {
          showError(t('departments:errors.delete_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deleteDepartment, showSuccess, showError, handleMenuClose, t]);

  const handleModuleToggle = useCallback(async (department, module, isEnabled) => {
    try {
      const result = isEnabled
        ? await disableModule(department.department_id, module.module_id)
        : await enableModule(department.department_id, module.module_id);

      if (result.success) {
        showSuccess(isEnabled ? t('departments:success.module_disabled') : t('departments:success.module_enabled'));
        const moduleResult = await getDepartmentModules(department.department_id);
        if (moduleResult.success) {
          moduleDialog.openDialog({
            department,
            assignedModules: moduleResult.data.modules || [],
          });
        }
      } else {
        showError(isEnabled ? t('departments:errors.module_disable_failed') : t('departments:errors.module_enable_failed'));
      }
    } catch (error) {
      showError(isEnabled ? t('departments:errors.module_disable_failed') : t('departments:errors.module_enable_failed'));
    }
  }, [enableModule, disableModule, showSuccess, showError, getDepartmentModules, moduleDialog, t]);

  const canCreate = permissions.hasScope('admin-department:create');
  const canUpdate = permissions.hasScope('admin-department:update');
  const canDelete = permissions.hasScope('admin-department:delete');
  const canResetOverdue = permissions.hasScope('admin-department:reset-overdue-tasks');
  const canAdminGenerateHolidays = permissions.hasScope('tidsregistering:non_working_day:admin_generate');

  const handleGenerateHolidaysClick = useCallback((department) => {
    generateHolidaysDialog.openDialog(department);
    handleMenuClose();
  }, [generateHolidaysDialog, handleMenuClose]);

  const handleGenerateHolidays = useCallback(async (department, dateRange) => {
    try {
      const result = await adminGenerateHolidaysForDepartment(department.department_id, dateRange);
      if (result.success) {
        showSuccess(t('departments:generate_holidays.success', { count: result.data.total ?? 0 }));
      } else {
        showError(result.error || t('departments:generate_holidays.errors.generate_failed'));
      }
      return result;
    } catch (error) {
      showError(t('departments:generate_holidays.errors.generate_failed'));
      return { success: false };
    }
  }, [adminGenerateHolidaysForDepartment, showSuccess, showError, t]);

  const handleResetOverdue = useCallback((department) => {
    showConfirmDialog({
      title: t('departments:confirm.reset_overdue_title'),
      message: t('departments:confirm.reset_overdue_message', { name: department.name }),
      confirmText: t('departments:confirm.reset_overdue_button'),
      severity: 'warning',
      onConfirm: async () => {
        try {
          const result = await resetOverdueTasks(department.department_id);
          if (result.success) {
            showSuccess(t('departments:success.reset_overdue', { count: result.data.reset_count }));
          } else {
            showError(result.error || t('departments:errors.reset_overdue_failed'));
          }
        } catch (error) {
          showError(t('departments:errors.reset_overdue_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, resetOverdueTasks, showSuccess, showError, handleMenuClose, t]);

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: t('departments:grid.columns.name'),
      flex: 1,
      minWidth: 260,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={2} sx={{ py: 1 }}>
          <Avatar sx={{ bgcolor: params.row.deactivated_at ? 'action.disabled' : 'primary.main', width: 32, height: 32 }}>
            <BusinessIcon fontSize="small" />
          </Avatar>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" fontWeight="medium">
                {params.value}
              </Typography>
              {params.row.deactivated_at && (
                <Chip
                  label={t('departments:status.deactivated')}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
              {params.row.manages_global_files && (
                <Tooltip title={t('departments:status.global_files_tooltip')}>
                  <Chip
                    icon={<PublicIcon />}
                    label={t('departments:status.global_files')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              <UUIDDisplay uuid={params.row.department_id} />
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'staff_count',
      headerName: t('departments:grid.columns.staff'),
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <PeopleIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.value ?? 0}</Typography>
        </Box>
      ),
    },
    {
      field: 'modules_count',
      headerName: t('departments:grid.columns.modules'),
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <ExtensionIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.value ?? 0}</Typography>
        </Box>
      ),
    },
    {
      field: 'deactivated_at',
      headerName: t('departments:grid.columns.status'),
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        params.value
          ? <Chip icon={<BlockIcon />} label={t('departments:status.deactivated')} size="small" color="warning" variant="outlined" />
          : <Chip icon={<CheckCircleIcon />} label={t('departments:status.active')} size="small" color="success" variant="outlined" />
      ),
    },
    {
      field: 'created_at',
      headerName: t('departments:grid.columns.created'),
      width: 150,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value ? <LocalizedDateDistance date={params.value} /> : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: t('departments:grid.columns.actions'),
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ], [handleMenuOpen, t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <PageHeader
        icon={BusinessIcon}
        title={t('departments:title')}
        subtitle={t('departments:subtitle')}
        actions={<>
          <Tooltip title={t('departments:actions.refresh')}>
            <IconButton onClick={handleRefresh} disabled={departmentsLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/departments/create')}
              disabled={departmentsLoading}
            >
              {t('departments:create_department')}
            </Button>
          )}
        </>}
      />

      {/* Error */}
      {departmentsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearDepartmentsError}>
          {departmentsError}
        </Alert>
      )}

      {/* Filter row */}
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder={t('departments:filter.search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={includeDeactivated}
              onChange={(e) => setIncludeDeactivated(e.target.checked)}
              size="small"
            />
          }
          label={t('departments:actions.include_deactivated')}
        />
      </Box>

      {/* DataGrid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-main': {
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                '&:focus': { outline: 'none' },
              },
              '& .MuiDataGrid-row': {
                '&:hover': { backgroundColor: 'action.hover' },
              },
            },
          },
        }}
      >
        <DataGrid
          rows={filteredDepartments}
          columns={columns}
          getRowId={(row) => row.department_id}
          loading={departmentsLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          rowHeight={72}
          getRowHeight={() => 'auto'}
          slots={{
            noRowsOverlay: () => (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                py={4}
              >
                <BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('departments:messages.no_departments')}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  {includeDeactivated
                    ? t('departments:messages.no_departments_system')
                    : t('departments:messages.no_departments_active')
                  }
                </Typography>
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/departments/create')}
                  >
                    {t('departments:messages.create_first_department')}
                  </Button>
                )}
              </Box>
            ),
          }}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Box>

      <DepartmentActionMenu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleMenuClose}
        department={selectedDepartment}
        onEdit={handleEditClick}
        onModules={canReadModules ? handleModulesClick : undefined}
        onDeactivate={handleDeactivate}
        onDelete={handleDelete}
        onResetOverdue={handleResetOverdue}
        onGenerateHolidays={handleGenerateHolidaysClick}
        onGlobalFiles={handleGlobalFiles}
        canUpdate={canUpdate}
        canDelete={canDelete}
        canResetOverdue={canResetOverdue}
        canAdminGenerateHolidays={canAdminGenerateHolidays}
      />

      <GenerateHolidaysDialog
        open={generateHolidaysDialog.open}
        department={generateHolidaysDialog.data}
        onClose={generateHolidaysDialog.closeDialog}
        onGenerate={handleGenerateHolidays}
        loading={generateHolidaysLoading}
      />

      <EditDepartmentDialog
        open={editDialog.open}
        onClose={editDialog.closeDialog}
        formData={formData}
        formErrors={formErrors}
        onFormChange={handleFormChange}
        onSubmit={handleEditSubmit}
        loading={departmentsLoading}
      />

      <ModulesManagementDialog
        open={moduleDialog.open}
        onClose={moduleDialog.closeDialog}
        department={moduleDialog.data?.department}
        assignedModules={moduleDialog.data?.assignedModules || []}
        allModules={modules}
        onModuleToggle={handleModuleToggle}
        canEnable={canEnable}
        canDisable={canDisable}
        loading={modulesLoading}
      />

      <ConfirmDialog />
    </Box>
  );
}
